import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "../app/store";
import { toast } from "react-toastify";
import apiClient from "../core/config/api";
import { BUCKET_LABELS, bucketForStatus } from "../services/verifierResultsService";
import { FREEZE_FOR_TESTING } from "./modalTestMode";
import TopUpCreditsModal from "./TopUpCreditsModal";
import "./ResolveCatchAllModal.css";

// Credits charged per address by the Catch-All Resolver (Catch-All
// addresses are re-checked). Fallback only: the backend's own price comes
// back from the summary route and is what is shown and charged.
export const CATCH_ALL_RESOLVE_CREDITS_PER_EMAIL = 2;

// Customer routes. The summary has the numbers only (fast for any file size);
// the email list is paged and loaded as the customer scrolls.
const summaryUrl = (requestId: number) => `/verifier-requests/re/${requestId}/resolve-catch-all/summary`;
const emailsUrl = (requestId: number) => `/verifier-requests/re/${requestId}/resolve-catch-all/emails`;
const PAGE_SIZE = 100;
const SEARCH_DEBOUNCE_MS = 300;

// Sample data used while FREEZE_FOR_TESTING (./modalTestMode) is on - the
// "not enough credits" state from the design.
const TEST_EMAIL_COUNT = 620;
const TEST_AVAILABLE_CREDITS = 963;
const TEST_DOMAINS = ["brightpath.co", "meridianlabs.io", "fernwood.com", "harboranalytics.com"];
const buildTestEmails = () =>
    Array.from({ length: TEST_EMAIL_COUNT }, (_, i) => ({
        email: `contactb${30 + i}@${TEST_DOMAINS[i % TEST_DOMAINS.length]}`,
        status: "catch_all",
    }));

export interface ResolveCatchAllSelection {
    /** Every resolvable address except `exclude_emails` ("Select All", then untick). */
    select_all?: boolean;
    exclude_emails?: string[];
    /** Exactly these addresses (ticked one by one). */
    emails?: string[];
    /** Then keep only the first N (a run sized to the balance). */
    limit?: number;
}

interface ResolveCatchAllModalProps {
    requestId: number;
    /** Estimate shown until the backend's exact count arrives (Catch-All). */
    emailCount: number;
    submitting: boolean;
    onClose: () => void;
    /** No argument = resolve everything. */
    onConfirm: (selection?: ResolveCatchAllSelection) => void;
}

type EmailRow = { email: string; status: string };

const formatNumber = (value: number) => value.toLocaleString("en-US");
const keyOf = (email: string) => email.trim().toLowerCase();

const WarningIcon = () => (
    <svg width="18" height="16" viewBox="0 0 14.357 13" aria-hidden="true">
        <g transform="translate(-1.463 -2.486)" fill="none" stroke="#DF5D5D" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15.123,12.988,9.8,3.662a1.33,1.33,0,0,0-2.316,0L2.16,12.988a1.332,1.332,0,0,0,1.164,2H13.972a1.332,1.332,0,0,0,1.151-2" strokeWidth="1" />
            <path d="M8.648,7.165v2.261" strokeWidth="1.3" />
            <path d="M8.649,12.321h.006" strokeWidth="1.5" />
        </g>
    </svg>
);

const errorText = (error: any, fallback: string) => {
    // A bare "Not Found" is the server saying the route does not exist
    // (backend not updated), not a message meant for the customer.
    const detail = error?.response?.data?.detail || error?.response?.data?.message;
    return typeof detail === "string" && detail !== "Not Found" ? detail : fallback;
};

const ResolveCatchAllModal = ({
    requestId,
    emailCount: realEmailCount,
    submitting,
    onClose,
    onConfirm: realOnConfirm,
}: ResolveCatchAllModalProps) => {
    // Members (sub-users, role 6) can't buy credits - no top-up link for them.
    const isMember = useSelector((state: RootState) => state.auth.roleId) === 6;
    const [view, setView] = useState<"summary" | "choose">("summary");
    const [showTopUp, setShowTopUp] = useState(false);

    // ---- Summary: count, price and balance only.
    const [exactCount, setExactCount] = useState<number | null>(null);
    const [availableCredits, setAvailableCredits] = useState<number | null>(null);
    const [creditsPerEmail, setCreditsPerEmail] = useState(CATCH_ALL_RESOLVE_CREDITS_PER_EMAIL);
    const [summaryLoading, setSummaryLoading] = useState(true);
    const [summaryError, setSummaryError] = useState<string | null>(null);

    const emailCount = FREEZE_FOR_TESTING ? TEST_EMAIL_COUNT : exactCount ?? realEmailCount;

    const onConfirm = FREEZE_FOR_TESTING
        ? (selection?: ResolveCatchAllSelection) =>
            toast.info(`Test mode: would resolve with ${JSON.stringify(selection ?? { all: true })} (no request sent)`)
        : realOnConfirm;

    useEffect(() => {
        let cancelled = false;
        if (FREEZE_FOR_TESTING) {
            setAvailableCredits(TEST_AVAILABLE_CREDITS);
            setSummaryLoading(false);
            return;
        }
        setSummaryLoading(true);
        setSummaryError(null);
        apiClient
            .get(summaryUrl(requestId), { params: { _t: Date.now() } })
            .then(({ data }) => {
                if (cancelled) return;
                setExactCount(Number(data?.count ?? 0));
                setAvailableCredits(Number(data?.available_credits ?? 0));
                if (Number(data?.credits_per_email) > 0) setCreditsPerEmail(Number(data.credits_per_email));
            })
            .catch((error) => {
                if (!cancelled) setSummaryError(errorText(error, "Unable to load the details. Please try again."));
            })
            .finally(() => {
                if (!cancelled) setSummaryLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [requestId]);

    // ---- Chooser: pages loaded as the list scrolls, optional search.
    const [rows, setRows] = useState<EmailRow[]>([]);
    const [page, setPage] = useState(0); // last page loaded; 0 = none yet
    const [hasMore, setHasMore] = useState(true);
    const [filteredTotal, setFilteredTotal] = useState<number | null>(null);
    const [pageLoading, setPageLoading] = useState(false);
    const [pageError, setPageError] = useState<string | null>(null);
    const [searchInput, setSearchInput] = useState("");
    // The search box is hidden for now (see the commented-out input below);
    // keep its setter referenced so bringing it back is just uncommenting.
    void setSearchInput;
    const [search, setSearch] = useState("");
    const requestSeqRef = useRef(0);
    const listRef = useRef<HTMLDivElement | null>(null);
    const sentinelRef = useRef<HTMLDivElement | null>(null);

    // Selection as a rule plus exceptions, so "Select All" covers every
    // address - loaded or not - without loading or sending the whole list.
    //   mode "all":  everything except `exceptions` (the unticked ones)
    //   mode "none": only `exceptions` (the ticked ones)
    const [selectMode, setSelectMode] = useState<"all" | "none">("all");
    const [exceptions, setExceptions] = useState<Set<string>>(new Set());
    const selectAllRef = useRef<HTMLInputElement | null>(null);

    const loadPage = useCallback(
        async (nextPage: number, term: string) => {
            const seq = ++requestSeqRef.current;
            setPageLoading(true);
            setPageError(null);
            try {
                let items: EmailRow[];
                let more: boolean;
                let total: number;
                if (FREEZE_FOR_TESTING) {
                    const all = buildTestEmails().filter((r) => !term || r.email.includes(term.toLowerCase()));
                    items = all.slice((nextPage - 1) * PAGE_SIZE, nextPage * PAGE_SIZE);
                    total = all.length;
                    more = nextPage * PAGE_SIZE < all.length;
                } else {
                    const { data } = await apiClient.get(emailsUrl(requestId), {
                        params: { page: nextPage, page_size: PAGE_SIZE, search: term || undefined },
                    });
                    items = Array.isArray(data?.emails) ? data.emails : [];
                    total = Number(data?.filtered_total ?? items.length);
                    more = !!data?.has_more;
                }
                if (seq !== requestSeqRef.current) return; // a newer search took over
                setRows((prev) => (nextPage === 1 ? items : [...prev, ...items]));
                setPage(nextPage);
                setHasMore(more);
                setFilteredTotal(total);
            } catch (error) {
                if (seq === requestSeqRef.current) setPageError(errorText(error, "Unable to load the emails. Please try again."));
            } finally {
                if (seq === requestSeqRef.current) setPageLoading(false);
            }
        },
        [requestId]
    );

    // Debounce the search box, then restart the list from page 1.
    useEffect(() => {
        const timer = window.setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
        if (view !== "choose") return;
        setRows([]);
        setPage(0);
        setHasMore(true);
        loadPage(1, search);
        if (listRef.current) listRef.current.scrollTop = 0;
    }, [view, search, loadPage]);

    // Load the next page when the bottom of the list scrolls into view.
    useEffect(() => {
        if (view !== "choose" || !sentinelRef.current || !listRef.current) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting && hasMore && !pageLoading && !pageError && page > 0) {
                    loadPage(page + 1, search);
                }
            },
            { root: listRef.current, rootMargin: "120px" }
        );
        observer.observe(sentinelRef.current);
        return () => observer.disconnect();
    }, [view, hasMore, pageLoading, pageError, page, search, loadPage]);

    const close = () => {
        if (!submitting) onClose();
    };

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            // The top-up modal on top handles its own Escape.
            if (e.key !== "Escape" || submitting || showTopUp) return;
            if (view === "choose") setView("summary");
            else onClose();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [submitting, onClose, view, showTopUp]);

    const totalCost = emailCount * creditsPerEmail;
    const insufficient = availableCredits !== null && totalCost > availableCredits;
    // How many addresses the current balance covers when it can't cover all.
    const affordableCount = insufficient
        ? Math.min(emailCount, Math.floor((availableCredits as number) / creditsPerEmail))
        : emailCount;
    const affordableCost = affordableCount * creditsPerEmail;

    const isChecked = (email: string) =>
        selectMode === "all" ? !exceptions.has(keyOf(email)) : exceptions.has(keyOf(email));
    const selectedCount = Math.max(0, selectMode === "all" ? emailCount - exceptions.size : exceptions.size);
    const selectedCost = selectedCount * creditsPerEmail;
    const selectionTooExpensive = availableCredits !== null && selectedCost > availableCredits;
    const allSelected = emailCount > 0 && selectMode === "all" && exceptions.size === 0;

    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate = selectedCount > 0 && !allSelected;
        }
    }, [selectedCount, allSelected, view]);

    const toggleAll = () => {
        setSelectMode(allSelected ? "none" : "all");
        setExceptions(new Set());
    };

    const toggleOne = (email: string) => {
        const key = keyOf(email);
        setExceptions((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const confirmSelection = () => {
        if (selectMode === "all") {
            onConfirm(exceptions.size ? { select_all: true, exclude_emails: Array.from(exceptions) } : { select_all: true });
        } else {
            onConfirm({ emails: Array.from(exceptions) });
        }
    };

    const chooserRows = useMemo(
        () =>
            rows.map((item) => {
                const bucket = bucketForStatus(item.status);
                return { ...item, bucket, label: BUCKET_LABELS[bucket] || item.status };
            }),
        [rows]
    );

    return (
        <div className="rca-overlay" onClick={close}>
            <div
                className="rca-modal"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="resolve-catch-all-title"
            >
                {view === "summary" ? (
                    <>
                        <div className="rca-header">
                            <h2 id="resolve-catch-all-title">Resolve Catch-All</h2>
                            <button type="button" className="rca-close" aria-label="Close" onClick={close} disabled={submitting}>
                                <img src="/images/v-close.svg" alt="" width={30} height={30} />
                            </button>
                        </div>

                        <p className="rca-desc">
                            Runs a deeper check on this file&apos;s Catch-All addresses to determine whether each is Valid or Invalid.
                        </p>

                        <div className="rca-cost">
                            <div className="rca-cost-row">
                                <span>{formatNumber(emailCount)} email{emailCount === 1 ? "" : "s"}</span>
                                <span>&times; {creditsPerEmail} credits</span>
                            </div>
                            <div className="rca-cost-row rca-cost-total">
                                <span>Total cost</span>
                                <span>{formatNumber(totalCost)} credits</span>
                            </div>

                            {insufficient && (
                                <div className="rca-alert" role="alert">
                                    <WarningIcon />
                                    <span>
                                        This needs {formatNumber(totalCost)} credits, but you only have {formatNumber(availableCredits as number)} available.
                                    </span>
                                </div>
                            )}
                        </div>

                        {insufficient ? (
                            <div className="rca-spacer" />
                        ) : (
                            <p className="rca-balance">
                                {summaryLoading
                                    ? "Checking available credits..."
                                    : summaryError && availableCredits === null
                                        ? summaryError
                                        : availableCredits === null
                                            ? null
                                            : `${formatNumber(availableCredits)} credits available · ${formatNumber(availableCredits - totalCost)} after this`}
                            </p>
                        )}

                        <button
                            type="button"
                            className="rca-submit"
                            onClick={() => onConfirm(insufficient ? { limit: affordableCount } : undefined)}
                            disabled={submitting || summaryLoading || emailCount === 0 || affordableCount === 0}
                            autoFocus
                        >
                            {submitting ? (
                                <span className="rca-spinner" />
                            ) : !insufficient ? (
                                "Resolve"
                            ) : affordableCount > 0 ? (
                                `Resolve ${formatNumber(affordableCount)} of them now · ${formatNumber(affordableCost)} credits`
                            ) : (
                                "Not enough credits"
                            )}
                        </button>

                        <div className="rca-links">
                            <button type="button" className="rca-link" onClick={() => setView("choose")} disabled={submitting || summaryLoading || emailCount === 0}>
                                Choose which emails to resolve
                            </button>
                            {!isMember && (
                                <button type="button" className="rca-link" onClick={() => setShowTopUp(true)} disabled={submitting}>
                                    Get more credits
                                </button>
                            )}
                        </div>
                    </>
                ) : (
                    <>
                        <div className="rca-header">
                            <div className="rca-header-title">
                                <button
                                    type="button"
                                    className="rca-back"
                                    aria-label="Back"
                                    onClick={() => setView("summary")}
                                    disabled={submitting}
                                >
                                    <svg width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                        <path d="M10 3 5 8l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                                <h2 id="resolve-catch-all-title">Email details</h2>
                            </div>
                            <button type="button" className="rca-close" aria-label="Close" onClick={close} disabled={submitting}>
                                <img src="/images/v-close.svg" alt="" width={30} height={30} />
                            </button>
                        </div>

                        <div className="rca-select-bar">
                            <label className="rca-check">
                                <input
                                    ref={selectAllRef}
                                    type="checkbox"
                                    checked={allSelected}
                                    onChange={toggleAll}
                                    disabled={emailCount === 0 || submitting}
                                />
                                <span>Select All</span>
                            </label>
                            <span className="rca-select-summary">
                                {formatNumber(selectedCount)} selected &middot; {formatNumber(selectedCost)} credits
                            </span>
                        </div>

                        {/* Search is hidden for now. Uncomment to bring it back; the
                            backend's search parameter and the debounce above already work.
                        <input
                            type="search"
                            id="rca-email-search"
                            className="rca-search"
                            placeholder="Search emails"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            disabled={submitting}
                            aria-label="Search emails"
                        />
                        */}

                        <div className="rca-email-list" role="group" aria-label="Catch-All emails" ref={listRef}>
                            {chooserRows.length === 0 && pageLoading ? (
                                <div className="rca-email-placeholder">
                                    <div className="spinner-border spinner-border-sm" role="status" />
                                    <span>Loading emails...</span>
                                </div>
                            ) : chooserRows.length === 0 && pageError ? (
                                <div className="rca-email-placeholder">
                                    <span>{pageError}</span>
                                    <button type="button" className="rca-link" onClick={() => loadPage(1, search)}>Retry</button>
                                </div>
                            ) : chooserRows.length === 0 && page > 0 ? (
                                <div className="rca-email-placeholder">
                                    <span>{search ? `No emails match "${search}".` : "No Catch-All emails in this file."}</span>
                                </div>
                            ) : (
                                <>
                                    {chooserRows.map((row) => (
                                        <label key={row.email} className="rca-email-row">
                                            <span className="rca-check">
                                                <input
                                                    type="checkbox"
                                                    checked={isChecked(row.email)}
                                                    onChange={() => toggleOne(row.email)}
                                                    disabled={submitting}
                                                />
                                                <span className="rca-email" title={row.email}>{row.email}</span>
                                            </span>
                                            <span className={`rca-email-status status-${row.bucket}`}>{row.label}</span>
                                        </label>
                                    ))}
                                    {(pageLoading || pageError) && chooserRows.length > 0 && (
                                        <div className="rca-load-more">
                                            {pageLoading ? (
                                                <>
                                                    <div className="spinner-border spinner-border-sm" role="status" />
                                                    <span>Loading more...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <span>{pageError}</span>
                                                    <button type="button" className="rca-link" onClick={() => loadPage(page + 1, search)}>Retry</button>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                            {/* Scrolling this into view loads the next page. */}
                            <div ref={sentinelRef} className="rca-sentinel" aria-hidden="true" />
                        </div>

                        {search && filteredTotal !== null && (
                            <p className="rca-search-note">
                                {formatNumber(filteredTotal)} matching &middot; Select All still covers all {formatNumber(emailCount)} emails
                            </p>
                        )}

                        {selectionTooExpensive && (
                            <div className="rca-alert rca-alert-standalone" role="alert">
                                <WarningIcon />
                                <span>
                                    {formatNumber(selectedCost)} credits needed, but you only have {formatNumber(availableCredits as number)} available.
                                </span>
                            </div>
                        )}

                        <button
                            type="button"
                            className="rca-submit rca-submit-chooser"
                            onClick={confirmSelection}
                            disabled={submitting || summaryLoading || selectedCount === 0 || selectionTooExpensive}
                        >
                            {submitting ? (
                                <span className="rca-spinner" />
                            ) : (
                                `Resolve ${formatNumber(selectedCount)} Email${selectedCount === 1 ? "" : "s"}`
                            )}
                        </button>
                    </>
                )}
            </div>

            {showTopUp && (
                // Rendered inside the overlay, so stop clicks on it from
                // bubbling up and closing this modal too.
                <div onClick={(e) => e.stopPropagation()}>
                    <TopUpCreditsModal onClose={() => setShowTopUp(false)} />
                </div>
            )}
        </div>
    );
};

export default ResolveCatchAllModal;
